'use client';

import * as React from 'react';
import Link from 'next/link';
import { allPosts } from 'content-collections';
import { format, isBefore } from 'date-fns';
import { ArrowRightIcon } from 'lucide-react';

import { baseUrl } from '@workspace/routes';
import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from '@workspace/ui/components/avatar';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@workspace/ui/components/pagination';

import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';
import { getInitials } from '~/lib/formatters';

const POSTS_PER_PAGE = 9; // 3x3 grid for optimal display

export function BlogPosts(): React.JSX.Element {
  const [currentPage, setCurrentPage] = React.useState(1);

  // Sort posts by publication date (newest first)
  const sortedPosts = React.useMemo(
    () =>
      allPosts
        .slice()
        .sort((a, b) => (isBefore(a.published, b.published) ? 1 : -1)),
    []
  );

  // Calculate pagination
  const totalPages = Math.ceil(sortedPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = sortedPosts.slice(startIndex, endIndex);

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of blog section for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <GridSection>
      <div className="container py-20 space-y-16">
        <SiteHeading
          badge="Blog Posts"
          title="Insights & News"
          description="Learn more from members of our team and industry-leading experts."
        />

        {/* Blog Posts Grid */}
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {currentPosts.map((post, index) => (
            <Link
              key={`${post.slug}-${index}`}
              href={`${baseUrl.Marketing}${post.slug}`}
              className="flex h-full flex-col justify-between rounded-2xl bg-background p-6 shadow-xs transition-shadow hover:shadow-md dark:bg-accent/30 dark:hover:bg-accent/50"
            >
              <div className="mb-4 flex items-center justify-between text-muted-foreground text-sm">
                <span>{post.category}</span>
                <time dateTime={post.published}>
                  {format(post.published, 'dd MMM yyyy')}
                </time>
              </div>

              <h2 className="mb-3 text-lg font-semibold md:text-xl">
                {post.title}
              </h2>

              <p className="mb-4 text-muted-foreground line-clamp-3">
                {post.description}
              </p>

              <div className="mt-auto flex items-center justify-between pt-4">
                <div className="flex items-center gap-2">
                  <Avatar className="size-7">
                    <AvatarImage
                      src={post.author?.avatar}
                      alt="avatar"
                    />
                    <AvatarFallback className="text-[10px]">
                      {getInitials(post.author?.name ?? '')}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{post.author?.name ?? ''}</span>
                </div>
                <div className="group flex items-center gap-2 text-sm hover:underline">
                  Read more
                  <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                      }
                    }}
                    className={
                      currentPage <= 1
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>

                {pageNumbers.map((page, index) => (
                  <PaginationItem key={index}>
                    {page === 'ellipsis' ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                      }
                    }}
                    className={
                      currentPage >= totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        {/* Page Info */}
        <div className="text-center text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, sortedPosts.length)} of{' '}
          {sortedPosts.length} posts
        </div>
      </div>
    </GridSection>
  );
}
